import { sentenceCase } from '@lbcamden/shared-utils';
import { Synchronizer } from 'tinybase/synchronizers';
import { MergeableStore } from 'tinybase/with-schemas';

import { GlobalStoreSchemas, PathPersister, RowIdKeys } from '~/common/types/tinybase';

import { ReferenceListItem, ReferenceListType } from '../types/data';
import { DisplayValueMaps } from '../utils/data';
import { GlobalStoreZodTables } from './globalStoreSchema';
import { SyncStoreWrapper } from './SyncStoreWrapper';

export type GlobalStoreRowIdKeys = typeof globalStoreRowIdKeys;

export const globalStoreRowIdKeys = {
  buildings: 'buildingId',
  fraBlocks: 'fraId',
  properties: 'propertyId',
  referenceLists: 'id',
  noteTypeSubtypes: 'id',
} satisfies RowIdKeys<GlobalStoreZodTables>;

export const referenceListFormatters: Partial<
  Record<ReferenceListType, (value: string) => string>
> = {
  PERSON_ATTRIBUTE: (value) => value.replace(/^.+:\s*/, ''), // Removes category prefix
  SUPPORTED_NLD: sentenceCase,
  RELATION: sentenceCase,
  HLD_END: sentenceCase,
  PERIODS: sentenceCase,
};

export class BaseGlobalStore extends SyncStoreWrapper<
  GlobalStoreZodTables,
  GlobalStoreSchemas,
  GlobalStoreRowIdKeys
> {
  protected referenceListsDisplayValueMaps: DisplayValueMaps = {};

  public constructor(
    store: MergeableStore<GlobalStoreSchemas>,
    persister?: PathPersister,
    synchronizer?: Synchronizer,
  ) {
    super(store, globalStoreRowIdKeys, persister, synchronizer);

    this.updateReferenceListDisplayValueMaps();

    this.store.addTableListener(
      'referenceLists',
      () => this.updateReferenceListDisplayValueMaps(),
      false,
    );
  }

  protected updateReferenceListDisplayValueMaps() {
    const maps: DisplayValueMaps = {};
    const referenceLists = Object.values(this.getTable('referenceLists')) as ReferenceListItem[];

    for (const { type, code, description } of referenceLists) {
      maps[type] ||= {};
      maps[type][code] = this.formatReferenceItemDescription(type, description);
    }
    this.referenceListsDisplayValueMaps = maps;
  }

  protected formatReferenceItemDescription(type: ReferenceListType, description: string) {
    return referenceListFormatters[type]?.(description) ?? description;
  }

  public referenceListDisplayValue = (type: ReferenceListType, value: string = '') =>
    this.referenceListsDisplayValueMaps[type]?.[value] || value;
}
